import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = isSignup ? "/auth/register" : "/auth/login";
      const payload = isSignup ? form : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      login(data.token, data.user);
      // Apply referral code if present in URL (?ref=CODE)
      if (isSignup) {
        const ref = new URLSearchParams(window.location.search).get("ref");
        if (ref) {
          try { await api.post("/referral/redeem", { code: ref }); toast.success("Referral applied"); }
          catch { /* silent — invalid/self referral */ }
        }
      }
      toast.success(isSignup ? "Account created" : "Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] blue-glow pointer-events-none" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-10" data-testid="auth-logo">
          <img src="/brand/auvra-logo-trim.png" alt="AUVRA" className="h-12 w-auto" />
        </Link>
        <div className="glass rounded-2xl p-8">
          <h1 className="text-2xl font-semibold tracking-tight mb-1">
            {isSignup ? "Create your account" : "Sign in to AUVRA"}
          </h1>
          <p className="text-secondary-auvra text-sm mb-8">
            {isSignup ? "Start investing in tokenized real-world assets." : "Access your investor dashboard."}
          </p>

          <div className="space-y-3 mb-6">
            <button onClick={googleLogin} data-testid="google-login-btn"
              className="w-full flex items-center justify-center gap-3 bg-white text-black font-medium py-3 rounded-xl hover:bg-white/90 transition-colors">
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" />
              Continue with Google
            </button>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-secondary-auvra">or with email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {isSignup && (
              <Field icon={User} placeholder="Full name" value={form.name} onChange={up("name")} testid="auth-name" required />
            )}
            <Field icon={Mail} type="email" placeholder="Email" value={form.email} onChange={up("email")} testid="auth-email" required />
            <Field icon={Lock} type="password" placeholder="Password" value={form.password} onChange={up("password")} testid="auth-password" required />
            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full flex items-center justify-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 disabled:opacity-60 text-white font-medium py-3.5 rounded-xl transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">
              {loading ? "Please wait…" : isSignup ? "Create account" : "Sign in"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="text-sm text-secondary-auvra text-center mt-6">
            {isSignup ? "Already have an account? " : "New to AUVRA? "}
            <Link to={isSignup ? "/login" : "/signup"} className="text-auvra-blue hover:underline" data-testid="auth-switch">
              {isSignup ? "Sign in" : "Create one"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 text-secondary-auvra absolute left-4 top-1/2 -translate-y-1/2" />
      <input {...props} data-testid={testid}
        className="w-full bg-black border border-white/15 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-auvra-blue transition-colors" />
    </div>
  );
}
